import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { RatingCard } from '../../Rating/RatingCard/RatingCard';
import ratingCardClasses from '../../Rating/RatingCard/RatingCard.module.scss';
import ratingLayoutClasses from '../../Rating/ratingCardLayout.module.scss';
import classes from './ModalRatingExtension.module.scss';

const TIMER_RADIUS = 52;
const TIMER_CIRCUMFERENCE = 2 * Math.PI * TIMER_RADIUS;

type ModalRatingExtensionProps = {
  show: boolean;
  countdownSeconds: number;
  textColor: string;
  blurredBackground?: boolean;
  onYes: () => void;
  onNo: () => void;
};

export const ModalRatingExtension: FC<ModalRatingExtensionProps> = ({
  show,
  countdownSeconds,
  textColor,
  blurredBackground = false,
  onYes,
  onNo
}) => {
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);
  const onNoRef = useRef(onNo);

  useEffect(() => {
    onNoRef.current = onNo;
  }, [onNo]);

  useEffect(() => {
    if (!show || countdownSeconds <= 0) {
      setSecondsLeft(countdownSeconds);
      return;
    }

    setSecondsLeft(countdownSeconds);
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          onNoRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [show, countdownSeconds]);

  const progressOffset = useMemo(() => {
    if (countdownSeconds <= 0) {
      return TIMER_CIRCUMFERENCE;
    }
    const progress = secondsLeft / countdownSeconds;
    return TIMER_CIRCUMFERENCE * (1 - progress);
  }, [secondsLeft, countdownSeconds]);

  if (!show || countdownSeconds <= 0) {
    return null;
  }

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true">
      <div className={ratingLayoutClasses.cardLayer}>
        <RatingCard
          textColor={textColor}
          blurredBackground={blurredBackground}
          className={ratingCardClasses.panelRating}
        >
          <div className={ratingLayoutClasses.ratingCardContent}>
            <p className={classes.question}>Нужно больше времени?</p>

            <div className={classes.timer} aria-live="polite" aria-atomic="true">
              <svg
                className={classes.timerRing}
                viewBox="0 0 120 120"
                aria-hidden="true"
              >
                <circle
                  className={classes.timerTrack}
                  cx="60"
                  cy="60"
                  r={TIMER_RADIUS}
                />
                <circle
                  className={classes.timerProgress}
                  cx="60"
                  cy="60"
                  r={TIMER_RADIUS}
                  strokeDasharray={TIMER_CIRCUMFERENCE}
                  strokeDashoffset={progressOffset}
                />
              </svg>
              <span className={classes.timerValue}>{secondsLeft}</span>
            </div>

            <div className={classes.actions}>
              <button
                type="button"
                className={classNames(classes.actionButton, classes.actionButtonNo)}
                onClick={onNo}
              >
                Нет
              </button>
              <button
                type="button"
                className={classNames(classes.actionButton, classes.actionButtonYes)}
                onClick={onYes}
              >
                Да
              </button>
            </div>
          </div>
        </RatingCard>
      </div>
    </div>
  );
};
